import { Injectable, inject, signal, computed } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
// DI tokens from AngularFire — used with inject() only
import { Auth } from '@angular/fire/auth';
import { Firestore } from '@angular/fire/firestore';
import { Functions } from '@angular/fire/functions';
// Firebase SDK functions imported directly to avoid AngularFire's injection-context
// wrapper, which warns and skips zone scheduling when called from async methods.
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  onAuthStateChanged,
  type User,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type { AppUser, UserRole } from '../models/user.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly auth = inject(Auth);
  private readonly firestore = inject(Firestore);
  private readonly functions = inject(Functions);

  readonly currentUser = signal<User | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);

  // Firestore profile — null until the snapshot returns the document
  readonly userProfile = signal<AppUser | null>(null);
  // True until the first Firestore snapshot resolves (prevents premature guard redirects)
  readonly profileLoading = signal(true);

  readonly isAuthenticated = computed(() => this.currentUser() !== null);
  readonly hasRole = computed(() => this.userProfile()?.role != null);

  // True once both Auth state and Firestore profile have settled
  readonly ready = computed(() => !this.loading() && !this.profileLoading());

  readonly isWithinGracePeriod = computed(() => {
    const createdAt = this.userProfile()?.createdAt as { toMillis(): number } | null;
    if (!createdAt) return false;
    // Date.now() is not a signal — value is frozen until userProfile next changes.
    // Acceptable for a 48h window; enforce server-side via createCheckoutSession.
    return Date.now() - createdAt.toMillis() < 48 * 60 * 60 * 1000;
  });

  readonly requiresEmailVerification = computed(() => {
    const user = this.currentUser();
    if (!user || user.emailVerified) return false;
    // userProfile is null on first snapshot (cache miss) before the server responds.
    // Don't redirect to verify-email until we know the grace period has actually expired.
    if (!this.userProfile()) return false;
    return !this.isWithinGracePeriod();
  });

  readonly showVerificationBanner = computed(
    () => !this.currentUser()?.emailVerified && this.isWithinGracePeriod()
  );

  // Observable versions — created here (injection context) so components can use
  // firstValueFrom() without triggering out-of-context warnings.
  readonly currentUser$ = toObservable(this.currentUser);
  readonly ready$ = toObservable(this.ready);

  // Manual unsubscribe rather than takeUntilDestroyed() — AuthService is providedIn: 'root'
  // and lives for the app lifetime, so there is no destroy event to hook into.
  // The listener is torn down explicitly when the Auth user signs out or changes.
  private profileUnsubscribe: (() => void) | null = null;

  constructor() {
    // Keep currentUser in sync with Firebase Auth state
    onAuthStateChanged(this.auth, (user) => {
      this.currentUser.set(user);
      this.loading.set(false);

      if (user) {
        this.subscribeToProfile(user.uid);
      } else {
        this.unsubscribeFromProfile();
        this.userProfile.set(null);
        this.profileLoading.set(false);
      }
    });
  }

  private subscribeToProfile(uid: string): void {
    this.profileLoading.set(true);
    const ref = doc(this.firestore, 'users', uid);
    // Tear down any existing listener before opening a new one
    this.unsubscribeFromProfile();
    this.profileUnsubscribe = onSnapshot(ref, (snap) => {
      this.userProfile.set(snap.exists() ? (snap.data() as AppUser) : null);
      this.profileLoading.set(false);
    });
  }

  private unsubscribeFromProfile(): void {
    if (this.profileUnsubscribe) {
      this.profileUnsubscribe();
      this.profileUnsubscribe = null;
    }
  }

  async signInWithEmail(email: string, password: string): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      await signInWithEmailAndPassword(this.auth, email, password);
    } catch (e) {
      this.error.set('auth.errors.invalidCredentials');
      throw e;
    } finally {
      this.loading.set(false);
    }
  }

  async signUpWithEmail(email: string, password: string): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const credential = await createUserWithEmailAndPassword(this.auth, email, password);
      await sendEmailVerification(credential.user, {
        url: `${window.location.origin}/auth/verify-email`,
      });
    } catch (e) {
      this.error.set('auth.errors.couldNotCreateAccount');
      throw e;
    } finally {
      this.loading.set(false);
    }
  }

  async signInWithGoogle(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      await signInWithPopup(this.auth, new GoogleAuthProvider());
    } catch (e) {
      this.error.set('auth.errors.googleSignInFailed');
      throw e;
    } finally {
      this.loading.set(false);
    }
  }

  // Apple sign-in deferred — method exists but is not surfaced in the UI
  async signInWithApple(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const provider = new OAuthProvider('apple.com');
      await signInWithPopup(this.auth, provider);
    } catch (e) {
      this.error.set('auth.errors.appleSignInFailed');
      throw e;
    } finally {
      this.loading.set(false);
    }
  }

  async sendPasswordReset(email: string): Promise<void> {
    this.error.set(null);
    try {
      await sendPasswordResetEmail(this.auth, email);
    } catch (e) {
      this.error.set('auth.errors.couldNotSendReset');
      throw e;
    }
  }

  async resendVerificationEmail(): Promise<void> {
    const user = this.currentUser();
    if (!user) return;
    await sendEmailVerification(user, {
      url: `${window.location.origin}/auth/verify-email`,
    });
  }

  async setUserRole(role: UserRole): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      // Best-effort token refresh so the CF receives email_verified: true.
      // user.reload() updates the user object but leaves the cached ID token stale.
      // Silently swallowed — if this fails the CF call below will produce the real error.
      await this.currentUser()?.getIdToken(true).catch(() => undefined);

      const fn = httpsCallable<{ role: UserRole }, { success: boolean }>(
        this.functions,
        'setUserRole'
      );
      await fn({ role });
    } catch (e) {
      this.error.set('auth.errors.couldNotSaveRole');
      throw e;
    } finally {
      this.loading.set(false);
    }
  }

  async signOut(): Promise<void> {
    await signOut(this.auth);
  }
}
